import type { SupportedCurrency } from '../utils/currency';

type CheckoutAttemptSelection = {
    planId: string;
    interval: 'monthly' | 'yearly';
    currency: SupportedCurrency;
    expectedDiscountGrantId?: string;
    uiMode?: 'hosted' | 'embedded';
    previewProjectId?: string;
    resultDigestId?: string;
};

const CHECKOUT_ATTEMPT_STORAGE_PREFIX = 'paperzilla_checkout_attempt_v1';
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function getCheckoutAttemptId(selection: CheckoutAttemptSelection): string {
    const storage = getSessionStorage();
    const storageKey = checkoutAttemptStorageKey(selection);
    const existingAttemptId = storage?.getItem(storageKey);

    if (existingAttemptId && uuidPattern.test(existingAttemptId)) {
        return existingAttemptId.toLowerCase();
    }

    const checkoutAttemptId = createCheckoutAttemptId();
    storage?.setItem(storageKey, checkoutAttemptId);
    return checkoutAttemptId;
}

export function clearCheckoutAttemptId(selection: CheckoutAttemptSelection) {
    getSessionStorage()?.removeItem(checkoutAttemptStorageKey(selection));
}

export function checkoutAttemptStorageKey(selection: CheckoutAttemptSelection): string {
    // Keep checkout intent out of this v1 key. A trial request must reuse a
    // pre-intent attempt once so the server can expire its stale immediate-pay
    // Stripe session, release the reserved offer, and tell the client to retry.
    return [
        CHECKOUT_ATTEMPT_STORAGE_PREFIX,
        selection.planId,
        selection.interval,
        selection.currency,
        selection.uiMode ?? 'hosted',
        selection.previewProjectId
            ? `preview-project-${selection.previewProjectId.toLowerCase()}`
            : 'all-projects',
        selection.resultDigestId
            ? `result-digest-${selection.resultDigestId.toLowerCase()}`
            : 'all-digests',
        selection.expectedDiscountGrantId
            ? `discount-grant-${selection.expectedDiscountGrantId.toLowerCase()}`
            : 'no-discount-grant',
    ].join(':');
}

function getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

function createCheckoutAttemptId(): string {
    const randomUUID = globalThis.crypto?.randomUUID;
    if (typeof randomUUID === 'function') {
        return randomUUID.call(globalThis.crypto);
    }

    const randomValues = globalThis.crypto?.getRandomValues;
    if (typeof randomValues === 'function') {
        const bytes = new Uint8Array(16);
        randomValues.call(globalThis.crypto, bytes);
        bytes[6] = (bytes[6] % 16) + 64;
        bytes[8] = (bytes[8] % 64) + 128;
        return formatUuidBytes(Array.from(bytes));
    }

    const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
    bytes[6] = (bytes[6] % 16) + 64;
    bytes[8] = (bytes[8] % 64) + 128;
    return formatUuidBytes(bytes);
}

function formatUuidBytes(bytes: number[]): string {
    return bytes
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}
